import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Grid,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  IconButton
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { PHARMACY_SALE_MODE, RECEIPT_MODE_INV } from '../../../../../constants/dropdown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const initialOpdPatientObj = {
  data: [],
  loading: false
};

const initialIpdPatientObj = {
  data: [],
  loading: false
};

const initialGeneralPatientObj = {
  data: [],
  loading: false
};

const initialReceiptObj = {
  data: [],
  loading: false
};

const OPD_PATIENT_TABLE_COLUMNS = [
  { header: 'Patient Name', accessor: 'patient_name' },
  { header: 'UHID No.', accessor: 'uhid' || '-' },
  { header: 'Mobile', accessor: 'mobile_no' || '-' },
  { header: 'Sale Mode', accessor: 'sale_mode' }
];

const GENERAL_PATIENT_TABLE_COLUMNS = [
  { header: 'Patient Name', accessor: 'patient_name' },
  { header: 'Balance', accessor: 'balance' },
  { header: 'Sale Mode', accessor: 'sale_mode' }
];

const IPD_PATIENT_TABLE_COLUMNS = [
  { header: 'Patient Name', accessor: 'patient_name' },
  { header: 'Ipd No.', accessor: 'ipd_no' || '-' },
  { header: 'UHID No.', accessor: 'uhid' || '-' },
  { header: 'Mobile', accessor: 'mobile_no' || '-' },
  { header: 'Sale Mode', accessor: 'sale_mode' }
];

const PATIENT_RECEIPT_TABLE_COLUMNS = [
  { header: 'Receipt No', accessor: 'receipt_no' },
  { header: 'Patient Name', accessor: 'patient_name' },
  { header: 'Ipd No.', accessor: 'ipd_no' || '-' },
  { header: 'UHID No.', accessor: 'uhid' || '-' },
  { header: 'Mobile', accessor: 'mobile' || '-' },
  { header: 'Receipt Mode', accessor: 'receipt_mode' },
  { header: 'Amount Paid', accessor: 'rec_amt' }
];

export const PatientReceiptVoucher = () => {
  const doctorIDRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opdPatientDataObj, setOpdPatientDataObj] = useState(initialOpdPatientObj);
  const [ipdPatientDataObj, setIpdPatientDataObj] = useState(initialIpdPatientObj);
  const [generalPatientDataObj, setGeneralPatientDataObj] = useState(initialGeneralPatientObj);
  const [receiptVoucherDataObj, setReceiptVoucherDataObj] = useState(initialReceiptObj);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [generalOpenQueryDialog, setGeneralOpenQueryDialog] = useState(false);
  const [IpdOpenQueryDialog, setIpdOpenQueryDialog] = useState(false);
  const [OpenQueryDialogs, setOpenQueryDialogs] = useState(false);
  const [ipdInputs, setIpdInputs] = useState('');
  const [opdInputs, setOpdInputs] = useState('');
  const [input, setInput] = useState('');
  const [labDetails, setLabDetails] = useState([]);
  const [serviceDetails, setServiceDetails] = useState([]);
  const [accountLedger, setAccountLedger] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        receipt_mode: Yup.string().required(' '),
        rec_amt: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5)
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue('', ['_id', 'status', 'createdAt']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      const formattedRegDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('patient_name', selectedRecord?.patient_name);
      setValue('uhid', selectedRecord?.uhid);
      setValue('receipt_no', selectedRecord?.receipt_no);
      setValue('mobile', selectedRecord?.mobile);
      setValue('ipd_no', selectedRecord?.ipd_no);
      setValue('rec_amt', selectedRecord?.rec_amt);
      setValue('ledger', selectedRecord?.ledger);
      setValue('receipt_mode', selectedRecord?.receipt_mode);
      setValue('remarks', selectedRecord?.remarks);
      setValue('payable_amt', selectedRecord?.payable_amt);
      setValue('type', selectedRecord?.type);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/patient-receipt-voucher-update' : '/patient-receipt-voucher-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...formValues,
        createdBy: user.name,
        updatedBy: editFlag ? user.name : '',
        organisation
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'Updated' : 'Created'} Patient Receipt Voucher`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Patient Receipt Voucher`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialogs(true);
    setReceiptVoucherDataObj({ loading: true });
    const response = await request('/patient-receipt-voucher-list', { method: 'GET' });
    if (response.success) {
      setReceiptVoucherDataObj({ loading: false, data: response.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setReceiptVoucherDataObj({ loading: false });
    }
    setLoading(false);
  };

  useEffect(() => {
    handleSearch();
  }, [input]);

  const handleSearch = async () => {
    setReceiptVoucherDataObj({ loading: true });
    const query = {};
    const lowercasedInput = input.toLowerCase().trim();
    if (lowercasedInput) {
      query.uhid = lowercasedInput;
    }
    const response = await request('/patient-receipt-voucher-list', { method: 'GET', query: query });
    if (response.success) {
      setReceiptVoucherDataObj({ loading: false, data: response.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setReceiptVoucherDataObj({ loading: false });
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setInput(e.target.value);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/retail-sale-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Retail Sale Pos', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Retail Sale Pos', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/retail-sale-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Retail Sale Pos', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Retail Sale Pos', { variant: 'error' });
    }
    setLoading(false);
  };

  const getAccountLedgerData = useCallback(async () => {
    const data = await dropdownFetch('account-ledger');
    setAccountLedger(data);
  }, []);

  useEffect(() => {
    getAccountLedgerData();
  }, [getAccountLedgerData]);

  const handleFetchRetailSaleOpdList = async () => {
    setOpenQueryDialog(true);
    setOpdPatientDataObj({ loading: true });
    try {
      const response = await request('/retail-sale-opd-credit-list', { method: 'GET' });
      if (response.success) {
        setOpdPatientDataObj({ loading: false, data: response?.data });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setOpdPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('Error fetching data', { variant: 'error' });
      setOpdPatientDataObj({ loading: false });
    }
  };

  const handleFetchRetailSaleIpdList = async () => {
    setIpdOpenQueryDialog(true);
    setIpdPatientDataObj({ loading: true });
    try {
      const response = await request('/retail-sale-ipd-credit-list', { method: 'GET' });
      if (response.success) {
        setIpdPatientDataObj({ loading: false, data: response?.data });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setIpdPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('Error fetching data', { variant: 'error' });
      setIpdPatientDataObj({ loading: false });
    }
  };

  useEffect(() => {
    handleSearchPatient();
  }, [ipdInputs]);

  const handleSearchPatient = async () => {
    setIpdPatientDataObj({ loading: true });
    try {
      const query = {};
      const lowercasedInput = ipdInputs.toLowerCase().trim();
      if (lowercasedInput) {
        query.uhid = lowercasedInput;
      }
      const response = await request('/retail-sale-ipd-credit-list', { method: 'GET', query: query });
      if (response.success) {
        setIpdPatientDataObj({ loading: false, data: response?.data });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setIpdPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('Error fetching data', { variant: 'error' });
      setIpdPatientDataObj({ loading: false });
    }
  };

  useEffect(() => {
    handleSearchOpdPatient();
  }, [opdInputs]);

  const handleSearchOpdPatient = async () => {
    setOpdPatientDataObj({ loading: true });
    try {
      const query = {};
      const lowercasedInput = opdInputs.toLowerCase().trim();
      if (lowercasedInput) {
        query.uhid = lowercasedInput;
      }
      const response = await request('/retail-sale-opd-credit-list', { method: 'GET', query: query });
      if (response.success) {
        setOpdPatientDataObj({ loading: false, data: response?.data });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setOpdPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('Error fetching data', { variant: 'error' });
      setOpdPatientDataObj({ loading: false });
    }
  };

  const handleIpdPatientChange = (e) => {
    setIpdInputs(e.target.value);
  };

  const handleOpdChange = (e) => {
    setOpdInputs(e.target.value);
  };

  const handleFetchRetailSaleGeneralList = async () => {
    setGeneralOpenQueryDialog(true);
    setGeneralPatientDataObj({ loading: true });
    try {
      const response = await request('/retail-sale-list', { method: 'GET' });
      if (response.success && Array.isArray(response.data)) {
        const filteredData = response.data.filter((item) => {
          return item.sale_mode === 'GENERAL' && item.pay_mode === 'Credit';
        });
        if (filteredData.length === 0) {
          toast('No matching data found for GENERAL and Credit mode.', { variant: 'info' });
        }
        setGeneralPatientDataObj({ loading: false, data: filteredData });
      } else {
        toast('Failed to load Retail Sale list', { variant: 'error' });
        setGeneralPatientDataObj({ loading: false });
      }
    } catch (error) {
      toast('An error occurred while loading Retail Sale list', { variant: 'error' });
      setGeneralPatientDataObj({ loading: false });
    }
  };

  const Type = watch('type');

  const handleGeneralPatient = (row) => {
    setValue('ledger', row?.ledger._id);
    setValue('patient_name', row?.patient_name);
    setValue('retail_no', row?.inv_no);
    setValue('payable_amt', row?.total_amt);
    setValue('disc_amt', row?.dis_amount);
    setValue('type', row?.sale_mode);
    setValue('uhid', row?.uhid);
    setGeneralOpenQueryDialog(false);
  };

  const handleOpdPatient = (row) => {
    setValue('ledger', row?.ledger);
    setValue('patient_name', row?.patient_name);
    setValue('mobile', row?.mobile_no);
    setValue('retail_no', row?.inv_no);
    setValue('payable_amt', row?.final_balance);
    setValue('disc_amt', row?.dis_amount);
    setValue('type', row?.sale_mode);
    setValue('uhid', row?.uhid);
    getPatientTotalBalance(row?.uhid);
    setOpenQueryDialog(false);
  };

  const handleIpdPatient = (row) => {
    setValue('ledger', row?.ledger);
    setValue('patient_name', row?.patient_name);
    setValue('mobile', row?.mobile_no);
    setValue('retail_no', row?.inv_no);
    setValue('payable_amt', row?.final_balance);
    setValue('disc_amt', row?.dis_amount);
    setValue('type', row?.sale_mode);
    setValue('uhid', row?.uhid);
    setValue('ipd_no', row?.ipd_no);
    getPatientTotalBalance(row?.uhid);
    setValue('retail_no', row?.inv_no);
    setIpdOpenQueryDialog(false);
  };

  const getPatientTotalBalance = async (ipdNo) => {
    setLoading(true);
    const response = await request('/patient-pending-balance', {
      method: 'GET',
      params: ipdNo
    });
    if (response?.success) {
      toast('Successfully Get The Balance', { variant: 'success' });
      setValue('payable_amt', response?.data);
      setSelectedRecord();
    } else {
      toast('Failed to get the Balance', { variant: 'error' });
    }
    setLoading(false);
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Patient Receipt Voucher
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    setServiceDetails([]);
                    setLabDetails([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={handleQueryWindow}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Grid container spacing={1}>
              <Grid item xs={11} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Stack
                  sx={{
                    border: '1px solid #d3d3d3',
                    padding: '5px',
                    borderRadius: '5px',
                    justifyContent: 'space-between',
                    padding: '15px'
                  }}
                  spacing={0.5}
                  direction="row"
                >
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Receipt No./ Date
                      </Typography>
                      <TextField disabled sx={{ flex: 0.4 }} name="receipt_no" />
                      <TextField
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{
                          flex: 0.4,
                          border: currentAction ? '0.5px solid red' : '',
                          backgroundColor: currentAction ? '#B3E5FC' : ''
                        }}
                        name="date"
                        type="date"
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        IPD/OPD No
                      </Typography>
                      <TextField disabled sx={{ flex: 0.4 }} name="ipd_no" placeholder="" />
                      <TextField disabled sx={{ flex: 0.4 }} name="uhid" placeholder="" />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Mobile No
                      </Typography>
                      <TextField disabled sx={{ flex: 0.8 }} name="mobile" placeholder="" />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Receipt Mode
                      </Typography>
                      <SelectTag
                        disabled={!['new', 'edit'].includes(currentAction)}
                        formControlSX={{ flex: 0.8 }}
                        name="receipt_mode"
                        placeholder=""
                        menus={RECEIPT_MODE_INV}
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Payable/Dis Amt.
                      </Typography>
                      <TextField disabled sx={{ flex: 0.4 }} name="payable_amt" placeholder="Pay Amt." />
                      <TextField
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 0.4 }}
                        name="disc_amt"
                        placeholder="Dis Amt."
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Type
                      </Typography>
                      <SelectTag
                        disabled={!['new', 'edit'].includes(currentAction)}
                        formControlSX={{ flex: 0.7 }}
                        name="type"
                        placeholder=""
                        menus={PHARMACY_SALE_MODE}
                      />
                      <Button
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }}
                        variant="contained"
                        onClick={() => {
                          if (Type === 'OPD') {
                            handleFetchRetailSaleOpdList();
                          } else if (Type === 'IPD') {
                            handleFetchRetailSaleIpdList();
                          } else {
                            handleFetchRetailSaleGeneralList();
                          }
                        }}
                      >
                        <SearchIcon />
                      </Button>
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Retail No.
                      </Typography>
                      <TextField
                        inputProps={{ style: { fontSize: '1.5rem', padding: '5px' } }}
                        disabled
                        sx={{ flex: 0.8 }}
                        name="retail_no"
                        placeholder="Retail No"
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Patient Name
                      </Typography>
                      <TextField
                        inputProps={{ style: { fontSize: '1.5rem', padding: '5px' } }}
                        disabled
                        sx={{ flex: 0.8 }}
                        name="patient_name"
                        placeholder="Enter Patient Name"
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Account
                      </Typography>
                      <SelectTag
                        disabled
                        formControlSX={{ flex: 0.8 }}
                        name="ledger"
                        placeholder=""
                        menus={accountLedger.map((data) => ({
                          ...data,
                          name: data.ledger_name
                        }))}
                      />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                        Rec. Amount
                      </Typography>
                      <TextField
                        inputProps={{ style: { fontSize: '1.5rem', padding: '5px' } }}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        sx={{ flex: 0.8 }}
                        name="rec_amt"
                        placeholder="Enter Amount"
                      />
                    </Stack>
                  </Grid>
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                    Remarks : 
                  </Typography>
                  <TextField
                    inputProps={{ style: { fontSize: '1.5rem', padding: '5px' } }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.8 }}
                    name="remarks"
                    placeholder="Remarks"
                  />
                </Stack>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '50%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={1.5}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Opd Patient Details
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0.5 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 10px',
                fontWeight: 700
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' 
              }
            }}
            label="Search Patient By UHID"
            variant="outlined"
            value={opdInputs}
            onChange={handleOpdChange}
          />
        </Stack>
        <Table
          sx={{ mt: 1 }}
          columns={OPD_PATIENT_TABLE_COLUMNS}
          data={opdPatientDataObj.data || []}
          loading={opdPatientDataObj.loading}
          onRowClick={(row) => {
            handleOpdPatient(row);
          }}
        />
      </Dialog>

      <Dialog
        open={generalOpenQueryDialog}
        onClose={() => setGeneralOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '50%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            General Patient Details
          </Typography>
          <IconButton onClick={() => setGeneralOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              }
            }}
            label="Search Patient"
            variant="outlined"
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={GENERAL_PATIENT_TABLE_COLUMNS}
          data={generalPatientDataObj.data || []}
          loading={opdPatientDataObj.loading}
          onRowClick={(row) => {
            handleGeneralPatient(row);
          }}
        />
      </Dialog>

      <Dialog
        open={IpdOpenQueryDialog}
        onClose={() => setIpdOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '50%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Ipd Patient Details
          </Typography>
          <IconButton onClick={() => setIpdOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px',
                fontWeight: 700
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 10px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' 
              }
            }}
            label="Search Patient By UHID"
            variant="outlined"
            value={ipdInputs}
            onChange={handleIpdPatientChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={IPD_PATIENT_TABLE_COLUMNS}
          data={ipdPatientDataObj.data || []}
          loading={ipdPatientDataObj.loading}
          onRowClick={(row) => {
            handleIpdPatient(row);
          }}
        />
      </Dialog>

      <Dialog
        open={OpenQueryDialogs}
        onClose={() => setOpenQueryDialogs(false)}
        PaperProps={{ sx: { width: '70%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient Receipt Voucher Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialogs(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px',
                fontWeight: 700
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 10px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem'
              }
            }}
            label="Search Patient By UHID"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={PATIENT_RECEIPT_TABLE_COLUMNS}
          data={receiptVoucherDataObj.data || []}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialogs(false);
          }}
          loading={receiptVoucherDataObj.loading}
        />
      </Dialog>
    </>
  );
};
